'use strict';

var _ = require('lodash');

var balanced = require('node-balanced');

var constants = require('./../constants');

var exceptions = require('./../exceptions');

var TOKEN_REGEX = constants.VARIABLE_TOKEN_REGEX;
var REPLACE_REGEX = constants.VARIABLE_TOKEN_REPLACE_REGEX;
var SUPPORTED_FUNCTIONS = constants.SUPPORTED_FUNCTIONS;
var REPLACEMENT_CHAR = '';
var VALID_OPEN_BRACKETS = ['{', '[', '('];
var VALID_CLOSE_BRACKETS = ['}', ']', ')']; // Matches all the defined operators as well as an optional - after them.
// this overcomes the situation where there may be 5+-5 but something like 5+--5 would be invalid
// the regex has an or clause for each operator the clause for each operator follows the same pattern.
// that pattern is: (?:${operator}(?:-?))

var MATH_OPERATOR_REGEX = new RegExp('' + getOperatorsRegex(), 'g');

var NEW_LINE = require('os').EOL;

var DUMMY_CHAR = '0';
var WORD_REGEX = /([A-Za-z]+)/g;
var MathExpressionComponentType = {
  term: 'term',
  operator: 'operator'
}; // If the function match Regex does not have the global flag it is possible for this to
// go into an infinite loop.

var FUNCTION_MATCH_REGEX = /(?:(\w+)(?:\(.*?\)))/gi;
var EMPTY_VARIABLE_NAME_MESSAGE = 'Variable name may not be empty';
/**
                                                                    * Returns a regex that will match all the supported operators and optionally a - sign after them
                                                                    * this is used to match expressions with possible negative numbers
                                                                    */

function getOperatorsRegex() {
  var opCheck = _.map(constants.SUPPORTED_OPERATORS, function (op) {
    return '(?:\\' + op + '(?:-?))';
  }).join('|');

  return '(' + opCheck + ')';
}

function getMissingVariableMessage(variableName) {
  return 'Missing ' + variableName + ' from expression context. Please provide a value for ' + variableName + '.';
}

function getCircularReferenceMessageForKey(key) {
  return 'Circular reference detected for key ' + key;
}

function getUnsupportedFunctionMessage(funcName) {
  return 'Unsupported function found ' + funcName;
}
/**
   * Given a valid  expression extract the unique variable names used in the expression
   * @param  {String} expression   An expression to pull the variable tokens out of.
   * @return {[String]}            Returns a list of variable names without the tokens surrounding the name.
   */


function extractVariableNames(expression) {
  if (!expression) {
    return [];
  }

  return _(expression.toString().match(TOKEN_REGEX)).map(function (token) {
    return token.replace(REPLACE_REGEX, REPLACEMENT_CHAR);
  }).uniq().value();
}
/**
   * Validate that all variable names are in the context
   * @param  {String} expression An expression to validate
   * @param  {Object} context    An object that should have a key for each variable in the expression
   * @return {Boolean}           Returns true if all variables in an expression are contained in the context object
   * @throws {Error}             If the context is missing variables an error will be thrown
   */


function validateExpressionVariables(expression, context, errors) {
  var variables = extractVariableNames(expression);
  errors = errors || [];

  _.each(variables, function (variableName) {
    if (_.isEmpty(variableName)) {
      errors.push(EMPTY_VARIABLE_NAME_MESSAGE);
    }

    if (_.isNil(context[variableName])) {
      errors.push(getMissingVariableMessage(variableName));
    }
  });

  if (!_.isEmpty(errors)) {
    throw new exceptions.InvalidContextException(errors.join(NEW_LINE));
  }

  return true;
}
/**
   * Validates that all variable tokens and () are balanced
   * @param  {String} expression The expression to check if it is balanced
   * @return {Boolean}           Returns true if the expression has balanced braces
   * @throws {Error}             if the expression is not balanced an error will be thrown
   */


function validateBalanced(expression) {
  // Fill the expression with dummy values to avoid issues with braces in variable names.
  var replacedExpression = expression.replace(TOKEN_REGEX, DUMMY_CHAR);
  var isBalanced = void 0;

  try {
    isBalanced = balanced.matches({
      source: replacedExpression,
      open: VALID_OPEN_BRACKETS,
      close: VALID_CLOSE_BRACKETS,
      balance: true,
      exceptions: true
    });
  } catch (error) {
    throw new exceptions.UnbalancedExpressionException(error.message);
  }

  return isBalanced;
}
/**
   * Returns a list of functions used in the expression
   * @param  {String}   expression to look for function calls in
   * @return {[String]} A list of functions used in the expression
   */


function getExpressionFunctions(expression) {
  var matches = [];
  var match = FUNCTION_MATCH_REGEX.exec(expression);

  while (match) {
    matches.push(match[1]);
    match = FUNCTION_MATCH_REGEX.exec(expression);
  }

  return matches;
}
/**
   * Validates all functions used in an expression are supported functions
   * @param  {String} expression  A formatted expression to evaluate
   * @return {Boolean}            Returns true if all functions in expression are supported
   * @throws {Error}              If an unsupported function is encountered
   */


function validateSupportedFunctions(expression) {
  var functions = getExpressionFunctions(expression);
  var errors = [];

  var unsupportedFuncs = _.difference(functions, SUPPORTED_FUNCTIONS);

  _.each(unsupportedFuncs, function (funcName) {
    errors.push(getUnsupportedFunctionMessage(funcName));
  });

  if (!_.isEmpty(errors)) {
    throw new exceptions.UnsupportedFunctionException(errors.join(NEW_LINE));
  }

  return true;
}
/**
   * Detects circular references in the given expression and context
   * @param  {String} expression The expression string to validate
   * @param  {Object} context    An object containing key value pairs that represent variable values.
   * @param  {Object} [stack]    Keeps track of expressions from the context that have been used in the current evaluation
   * @return {Boolean}           True if there are no circular references in the context
   * @throws {Error}             If a circular reference is detected an error will be thrown
   */


function validateNoCircularReferences(expression, context, stack) {
  stack = stack || {};
  var vars = extractVariableNames(expression);

  _.each(vars, function (variableName) {
    if (stack[variableName]) {
      throw new exceptions.CircularReferenceException(getCircularReferenceMessageForKey(variableName));
    }

    var newStack = _.cloneDeep(stack);

    newStack[variableName] = true;
    var varVal = context[variableName];

    if (varVal && varVal.toString().match(TOKEN_REGEX)) {
      validateNoCircularReferences(context[variableName], context, newStack);
    }
  });

  return true;
}
/**
   * Given a string return if the value is an operator or a term
   * @param  {string} the value to determine the Component type of
   * @return {MathExpressionComponentType} the Component type of the value
   */


function getExpressionComponentType(val) {
  return val.match(MATH_OPERATOR_REGEX) ? MathExpressionComponentType.operator : MathExpressionComponentType.term;
}
/**
   * Returns the next expression type expected based on the currentType passed in.
   * @param  {MathExpressionComponentType} the current expression type being looked for
   * @return {MathExpressionComponentType} the next kind of expression term to expect
   */


function getNextExpressionComponentType(currentType) {
  return currentType === MathExpressionComponentType.operator ? MathExpressionComponentType.term : MathExpressionComponentType.operator;
}
/**
   * Validates that the expressions conforms to the expected format of a math equation
   * @param  {String} expression - the string expression to check if valid
   * @return {Boolean} returns true if the given expression is valid
   * @throws {InvalidMathTermsException} If the expression has invalid or missing math operations
   */


function validateMathOperations(expression) {
  var splitExpression = _.reject(expression.trim().replace(TOKEN_REGEX, DUMMY_CHAR).split(MATH_OPERATOR_REGEX), _.isEmpty);

  var nextExpectedTermType = MathExpressionComponentType.term;

  if (getExpressionComponentType(_.last(splitExpression)) === MathExpressionComponentType.operator) {
    throw new exceptions.InvalidMathTermsException('Expected last element to be a term. Found operator (' + _.last(splitExpression) + ')');
  } // Since -- and - are ok to start an expression with we allow it here


  if (_.first(splitExpression).match(/(-(?:-?))/)) {
    nextExpectedTermType = MathExpressionComponentType.operator;
  }

  _.each(splitExpression, function (val) {
    if (getExpressionComponentType(val) !== nextExpectedTermType) {
      throw new exceptions.InvalidMathTermsException('Expected expression type ' + nextExpectedTermType + '. Got ' + val);
    }

    nextExpectedTermType = getNextExpressionComponentType(nextExpectedTermType);
  });

  return true;
}
/**
   * Validates that there are no invalid strings inside the given expression string
   * @param  {string} expression the expression to validate
   * @return {Boolean} returns true if the expression has no invalid strings
   * @throws {UnknownStringException} thrown when an unknown string is encountered
   */


function validateExpressionContent(expression) {
  // Fill the expression with dummy values to avoid complications with variables
  var replacedExpression = expression.replace(TOKEN_REGEX, DUMMY_CHAR);
  var matches = replacedExpression.match(WORD_REGEX);
  var errors = [];

  _.each(matches, function (match) {
    if (_.indexOf(constants.SUPPORTED_FUNCTIONS, match) < 0) {
      errors.push('Unknown string value ' + match + ' found in expression.');
    }
  });

  if (errors.length > 0) {
    throw new exceptions.UnknownStringException(errors.join(NEW_LINE));
  }

  return true;
}
/**
   * Gets all the variables used in a given expression. This will recursivly check for variables
   * in the context expressions; Make sure this is run after the check for circular references or this may cause
   * an infinite loop
   * @param  {String} expression [description]
   * @param  {Object} context    [description]
   * @param  {Object} variables  [description]
   * @return {[String]}          [description]
   */


function getExpressionVariables(expression, context, variables) {
  variables = variables || {};

  _.each(extractVariableNames(expression), function (varName) {
    variables[varName] = true;
    getExpressionVariables(context[varName], context, variables);
  });

  return _.keys(variables);
}
/**
   * Validates all the variables used in the given expression conform to our standards
   * @param  {[type]} expression [description]
   * @param  {[type]} context    [description]
   * @return {[type]}            [description]
   */


function validateContextExpressions(expression, context) {
  _.each(getExpressionVariables(expression, context), function (varName) {
    if (_.has(context, varName)) {
      try {
        validateMathOperations(context[varName].toString());
        validateExpressionContent(context[varName].toString());
      } catch (e) {
        e.message = 'Error in context key: ' + varName + '. ' + e.message;
        throw e;
      }
    } else {
      throw new exceptions.InvalidContextException(getMissingVariableMessage(varName));
    }
  });
}
/**
   * Handles validation of an expression
   * Given an expression and a context, does the context contain all the necessary variables for the expression to be evaluated
   * is the expression a valid expression
   * @param  {String} expression The expression to validate
   * @param  {Object} context    The context for the expression
   * @return {Boolean}           Returns true if the expression is valid.
   * @throws {Error}             If the expression is invalid an error will be thrown
   */


function validate(expression, context) {
  if (_.isEmpty(expression)) {
    return true;
  }

  validateBalanced(expression);
  validateSupportedFunctions(expression);
  validateExpressionVariables(expression, context);
  validateNoCircularReferences(expression, context);
  validateMathOperations(expression);
  validateExpressionContent(expression);
  validateContextExpressions(expression, context);
  return true;
}
/**
   * Validates an array of expressions against a single context
   * @param  {[String]} expressions An array of expression strings
   * @param  {Object}   context     The variables used in the expressions
   * @return {[Object]}             An array of validation responses
   */


function validateBatch(expressions, context) {
  return _.map(expressions, function (expression) {
    var val = {
      key: expression,
      isValid: true
    };

    try {
      validate(expression, context);
    } catch (exception) {
      val.isValid = false;
      val.error = exception;
    }

    return val;
  });
}

module.exports.validate = validate;
module.exports.validateBatch = validateBatch;